import { useEffect, useRef, useState } from 'react'
import {
  backupFileName,
  parseBackup,
  serializeBackup,
  summarizeBackup,
  type Backup,
} from '../../domain'
import { downloadText, readFileText } from '../../platform/download'
import { isPersisted } from '../../platform/persistence'
import { getDb } from '../../storage/db'
import { exportBackup, markBackupDone, restoreBackup } from '../../storage/backup'
import {
  DEFAULT_PREFERENCES,
  PEDAL_ACTIONS,
  keyId,
  parsePreferences,
  type PedalAction,
} from '../../storage/preferences'
import { getMeta } from '../../storage/repository'
import common from '../common.module.css'
import { useLive } from '../hooks/useLive'
import { Calibration } from '../settings/Calibration'
import { getPreferences, replacePreferences, updatePreferences, usePreferences } from '../prefs'
import { paths } from '../router'
import { formatAgo, t } from '../strings'
import styles from './Settings.module.css'

const MIN_SIZE = 14
const MAX_SIZE = 64

/** Los nombres de tecla que se ven mejor de otra manera. */
function keyLabel(key: string): string {
  return (
    (
      { ' ': 'Space', ArrowLeft: '←', ArrowRight: '→', ArrowUp: '↑', ArrowDown: '↓' } as Record<
        string,
        string
      >
    )[key] ?? key
  )
}

export function Settings() {
  const preferences = usePreferences()
  const s = t.settings

  return (
    <div className={common.page}>
      <h1 className={common.pageTitle}>{s.title}</h1>

      <section className={common.card}>
        <h2>{s.sheetTitle}</h2>
        <div className={styles.group}>
          <span className={styles.label}>{s.notation}</span>
          {(['american', 'latin'] as const).map((value) => (
            <button
              key={value}
              aria-pressed={preferences.notation === value}
              className={preferences.notation === value ? common.toggleOn : undefined}
              onClick={() => updatePreferences((current) => ({ ...current, notation: value }))}
            >
              {value === 'american' ? t.play.notationAmerican : t.play.notationLatin}
            </button>
          ))}
        </div>
        <p className={`${common.muted} ${common.small}`}>{s.notationHint}</p>
        <div className={styles.group}>
          <span className={styles.label}>{s.theme}</span>
          {(
            [
              ['dark', t.play.themeDark],
              ['light', t.play.themeLight],
              ['contrast', t.play.themeContrast],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              aria-pressed={preferences.theme === value}
              className={preferences.theme === value ? common.toggleOn : undefined}
              onClick={() => updatePreferences((current) => ({ ...current, theme: value }))}
            >
              {label}
            </button>
          ))}
        </div>
        <div className={styles.group}>
          <span className={styles.label}>{s.fontSize}</span>
          <button
            aria-label={`${s.fontSize} −`}
            disabled={preferences.fontSize <= MIN_SIZE}
            onClick={() =>
              updatePreferences((current) => ({
                ...current,
                fontSize: Math.max(MIN_SIZE, current.fontSize - 2),
              }))
            }
          >
            {t.play.smaller}
          </button>
          <span className={styles.size}>{preferences.fontSize}</span>
          <button
            aria-label={`${s.fontSize} +`}
            disabled={preferences.fontSize >= MAX_SIZE}
            onClick={() =>
              updatePreferences((current) => ({
                ...current,
                fontSize: Math.min(MAX_SIZE, current.fontSize + 2),
              }))
            }
          >
            {t.play.larger}
          </button>
        </div>
        <div className={styles.group}>
          <span className={styles.label}>{t.play.awake}</span>
          <button
            aria-pressed={preferences.keepAwake}
            className={preferences.keepAwake ? common.toggleOn : undefined}
            onClick={() =>
              updatePreferences((current) => ({ ...current, keepAwake: !current.keepAwake }))
            }
          >
            {s.keepAwake}
          </button>
        </div>
      </section>

      <PedalSettings />
      <Calibration />
      <BackupSettings />
      <StorageInfo />

      <section className={common.card}>
        <h2>{s.diagnostics.title}</h2>
        <p className={common.muted}>{s.diagnostics.text}</p>
        <div className={common.row}>
          <a href={`#${paths.clockSpike}`}>{s.diagnostics.clock}</a>
          <a href={`#${paths.deviceSpike}`}>{s.diagnostics.device}</a>
        </div>
        <p className={`${common.muted} ${common.small}`}>{s.diagnostics.console}</p>
        <p className={`${common.muted} ${common.small}`}>
          {t.app.name} {__APP_VERSION__} · {new Date(__BUILD_TIME__).toLocaleString('es-ES')}
        </p>
      </section>
    </div>
  )
}

/** Teclas del pedal: se aprenden pulsándolas, porque un pedal Bluetooth llega como un teclado. */
function PedalSettings() {
  const preferences = usePreferences()
  const s = t.settings
  const [listening, setListening] = useState<PedalAction | null>(null)

  useEffect(() => {
    if (listening === null) return
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return
      event.preventDefault()
      event.stopPropagation()
      const key = keyId(event)
      updatePreferences((current) => {
        // Una tecla hace una sola cosa: se quita de las demás acciones.
        const pedal = { ...current.pedal }
        for (const action of PEDAL_ACTIONS) {
          pedal[action] = pedal[action].filter((existing) => existing !== key)
        }
        pedal[listening] = [...pedal[listening], key].slice(0, 12)
        return { ...current, pedal }
      })
      setListening(null)
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [listening])

  const remove = (action: PedalAction, key: string) =>
    updatePreferences((current) => ({
      ...current,
      pedal: { ...current.pedal, [action]: current.pedal[action].filter((item) => item !== key) },
    }))

  return (
    <section className={common.card}>
      <h2>{s.pedalTitle}</h2>
      <p className={common.muted}>{s.pedalHint}</p>
      {PEDAL_ACTIONS.map((action) => (
        <div key={action} className={styles.pedalRow}>
          <span className={styles.label}>{s.actions[action]}</span>
          <span className={styles.keys}>
            {preferences.pedal[action].length === 0 && (
              <span className={common.muted}>{s.noKeys}</span>
            )}
            {preferences.pedal[action].map((key) => (
              <span key={key} className={styles.key}>
                {keyLabel(key)}
                <button aria-label={s.removeKey(key)} onClick={() => remove(action, key)}>
                  ×
                </button>
              </span>
            ))}
          </span>
          {listening === action ? (
            <span className={common.row}>
              <span className={styles.listening} role="status">
                {s.listening}
              </span>
              <button onClick={() => setListening(null)}>{s.cancel}</button>
            </span>
          ) : (
            <button onClick={() => setListening(action)}>{s.addKey}</button>
          )}
        </div>
      ))}
      <div className={common.row} style={{ marginTop: 10 }}>
        <button
          onClick={() =>
            updatePreferences((current) => ({
              ...current,
              pedal: structuredClone(DEFAULT_PREFERENCES.pedal),
            }))
          }
        >
          {s.resetPedal}
        </button>
      </div>
    </section>
  )
}

interface Pending {
  backup: Backup
  warnings: string[]
}

function BackupSettings() {
  const b = t.settings.backup
  const { data: stats } = useLive(async () => {
    const db = getDb()
    const [lastBackupAt, songs] = await Promise.all([getMeta(db, 'lastBackupAt'), db.songs.count()])
    // La hora se lee aquí y no al pintar: pintar tiene que dar siempre lo mismo.
    return {
      lastBackupAt: typeof lastBackupAt === 'number' ? lastBackupAt : null,
      songs,
      now: Date.now(),
    }
  }, [])
  const fileInput = useRef<HTMLInputElement>(null)
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)
  const [pending, setPending] = useState<Pending | null>(null)
  const [confirmReplace, setConfirmReplace] = useState(false)
  const [busy, setBusy] = useState(false)

  async function save() {
    setBusy(true)
    setMessage(null)
    try {
      const db = getDb()
      const now = new Date().toISOString()
      const backup = await exportBackup(db, now, { ...getPreferences() })
      downloadText(backupFileName(now), serializeBackup(backup), 'application/json')
      await markBackupDone(db, now)
      setMessage({ ok: true, text: b.exported(backup.songs.length) })
    } catch (cause) {
      setMessage({
        ok: false,
        text: b.exportError(cause instanceof Error ? cause.message : String(cause)),
      })
    } finally {
      setBusy(false)
    }
  }

  async function pick(file: File) {
    setMessage(null)
    setConfirmReplace(false)
    const parsed = parseBackup(await readFileText(file))
    if (!parsed.ok) {
      setPending(null)
      setMessage({ ok: false, text: b.error(parsed.errors.join(' ')) })
      return
    }
    setPending({ backup: parsed.backup, warnings: parsed.warnings.map((issue) => issue.message) })
  }

  async function restore(mode: 'replace' | 'merge') {
    if (!pending) return
    setBusy(true)
    try {
      const report = await restoreBackup(getDb(), pending.backup, mode, new Date().toISOString())
      if (mode === 'replace' && pending.backup.preferences) {
        replacePreferences(parsePreferences(pending.backup.preferences))
      }
      const added = report.added.songs + report.added.arrangements + report.added.revisions
      const skipped = report.skipped.songs + report.skipped.arrangements + report.skipped.revisions
      setMessage({ ok: true, text: b.done(added, skipped) })
      setPending(null)
      setConfirmReplace(false)
    } catch (cause) {
      setMessage({
        ok: false,
        text: b.error(cause instanceof Error ? cause.message : String(cause)),
      })
    } finally {
      setBusy(false)
    }
  }

  const summary = pending ? summarizeBackup(pending.backup) : null

  return (
    <section className={common.card}>
      <h2>{b.title}</h2>
      <p className={common.muted}>{b.text}</p>
      <p>
        {stats?.lastBackupAt ? b.last(formatAgo(stats.now - stats.lastBackupAt)) : b.never}{' '}
        {stats && <span className={common.muted}>{b.count(stats.songs)}</span>}
      </p>
      <div className={common.row}>
        <button className="primary" disabled={busy} onClick={() => void save()}>
          {b.export}
        </button>
        <button disabled={busy} onClick={() => fileInput.current?.click()}>
          {b.restore}
        </button>
        <input
          ref={fileInput}
          className={styles.file}
          type="file"
          accept=".json,application/json"
          onChange={(event) => {
            const file = event.target.files?.[0]
            event.target.value = ''
            if (file) void pick(file)
          }}
        />
      </div>

      {message && (
        <p className={message.ok ? common.notice : common.noticeError} role="status">
          {message.text}
        </p>
      )}

      {pending && summary && (
        <div className={`${common.noticeWarn} ${styles.restore}`}>
          <p style={{ margin: '0 0 8px' }}>
            {b.summary(
              summary.songs,
              summary.arrangements,
              summary.revisions,
              new Date(pending.backup.exportedAt).toLocaleString('es-ES'),
            )}
          </p>
          {pending.warnings.length > 0 && (
            <ul className={styles.list}>
              {pending.warnings.slice(0, 5).map((warning, index) => (
                <li key={index}>{warning}</li>
              ))}
            </ul>
          )}
          <p className={`${common.muted} ${common.small}`}>{b.mergeHint}</p>
          {confirmReplace && <p className={common.small}>{b.replaceWarn}</p>}
          <div className={common.row}>
            <button className="primary" disabled={busy} onClick={() => void restore('merge')}>
              {b.merge}
            </button>
            {confirmReplace ? (
              <button
                className={common.danger}
                disabled={busy}
                onClick={() => void restore('replace')}
              >
                {b.replace}
              </button>
            ) : (
              <button disabled={busy} onClick={() => setConfirmReplace(true)}>
                {b.replace}
              </button>
            )}
            <button
              onClick={() => {
                setPending(null)
                setConfirmReplace(false)
              }}
            >
              {b.cancel}
            </button>
          </div>
        </div>
      )}
    </section>
  )
}

function StorageInfo() {
  const s = t.settings.storage
  const [persisted, setPersisted] = useState<boolean | null | undefined>(undefined)
  useEffect(() => {
    let cancelled = false
    void isPersisted().then((value) => {
      if (!cancelled) setPersisted(value)
    })
    return () => {
      cancelled = true
    }
  }, [])
  return (
    <section className={common.card}>
      <h2>{s.title}</h2>
      <p>
        {s.persistent}:{' '}
        <strong>
          {persisted === undefined
            ? '…'
            : persisted === null
              ? s.unknown
              : persisted
                ? s.yes
                : s.no}
        </strong>
      </p>
    </section>
  )
}
